import type { SenderInfoOutput, SenderUpdateInput } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetSenderById, useUpdateSender } from "@/services";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	toast,
} from "@st-marketplace/ui";
import { useCallback, useEffect, useState } from "react";
import NewFormSenderForm, {
	type NewFormSenderFormData,
} from "./New/FormSender/Form";

const INITIAL_FORM_DATA: NewFormSenderFormData = {
	reserved_balance: null,
	use_inherited_keep: true,
	use_inherited_priority: true,
	proxy: "",
	email: "",
	password: "",
	auth_type: "gauth",
	gauth: "",
	backup_code: [],
	imap_server: "",
	imap_port: null,
	imap_username: "",
	imap_password: "",
	imap_ssl: false,
	sender_group_name: "",
	priority: null,
};

const toFormData = (s?: SenderInfoOutput): NewFormSenderFormData => {
	if (!s) return INITIAL_FORM_DATA;

	const hasDirectValue =
		s.this_sender_to_keep !== null && s.this_sender_to_keep !== undefined;

	const hasDirectPriority =
		s.this_sender_priority !== null && s.this_sender_priority !== undefined;

	const common = {
		reserved_balance: s.this_sender_to_keep ?? null,
		use_inherited_keep: !hasDirectValue,
		use_inherited_priority: !hasDirectPriority,
		proxy: s.proxy?.id ?? "",
		email: s.ea_email ?? "",
		password: s.ea_password ?? "",
		sender_group_name: s.group_id ?? "",
		priority: s.this_sender_priority ?? null,
	};

	switch (s.auth.type) {
		case "gauth":
			return {
				...common,
				auth_type: "gauth",
				gauth: s.auth.auth,
				backup_code: [],
				imap_server: "",
				imap_port: null,
				imap_username: "",
				imap_password: "",
				imap_ssl: false,
			};

		case "backup_code":
			return {
				...common,
				auth_type: "backupCode",
				gauth: "",
				backup_code: s.auth.auth,
				imap_server: "",
				imap_port: null,
				imap_username: "",
				imap_password: "",
				imap_ssl: false,
			};

		case "imap":
			return {
				...common,
				auth_type: "imap",
				gauth: "",
				backup_code: [],
				imap_server: s.auth.server ?? "",
				imap_port: s.auth.port ?? null,
				imap_username: s.auth.username ?? "",
				imap_password: s.auth.password ?? "",
				imap_ssl: !!s.auth.ssl,
			};

		default:
			return { ...INITIAL_FORM_DATA, ...common };
	}
};

interface SendersEditDialogProps {
	senderId?: string;
	values?: SenderInfoOutput;
	open: boolean;
	onClose: () => void;
}

const SendersEditDialog: React.FC<SendersEditDialogProps> = ({
	senderId,
	values,
	open,
	onClose,
}) => {
	const { t } = useTranslation();
	const updateSenderMutation = useUpdateSender();

	const shouldFetch = Boolean(senderId) && open;
	const { data: senderInfo, isFetching } = useGetSenderById(
		{ path: { sender_id: senderId as string } },
		{ enabled: shouldFetch },
	);

	const source = senderInfo ?? values;
	const [formData, setFormData] = useState<NewFormSenderFormData>(() =>
		toFormData(source),
	);

	useEffect(() => {
		setFormData(toFormData(source));
	}, [source?.id, source]);

	const handleReset = () => {
		setFormData(toFormData(source));
	};

	const handleFormChange = useCallback(
		(name: keyof NewFormSenderFormData, value: string) => {
			setFormData((prevData) => ({
				...prevData,
				[name]: value,
			}));
		},
		[],
	);

	const handleSubmit = (data: NewFormSenderFormData) => {
		const payload: SenderUpdateInput = {
			to_keep: data.reserved_balance,
			ea_email: data.email,
			ea_password: data.password,
			auth:
				data.auth_type === "gauth"
					? { type: "gauth", auth: data.gauth }
					: data.auth_type === "backupCode"
						? { type: "backup_code", auth: data.backup_code }
						: {
								type: "imap",
								username: data.imap_username,
								password: data.imap_password,
								server: data.imap_server,
								port: data.imap_port as number,
								ssl: data.imap_ssl,
							},
			sender_group_id: data.sender_group_name,
			priority_usage: data.use_inherited_priority
				? null
				: data.priority ?? null,
		};

		updateSenderMutation.mutate(
			{ path: { sender_id: values?.id || senderId || "" }, body: payload },
			{
				onSuccess: () => {
					toast.success(t("editSender.successMessage"));
					onClose();
				},
			},
		);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="w-full max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t("senderInfo.tabInfo")}</DialogTitle>
				</DialogHeader>
				<DialogScrollableArea className="max-h-[90vh]">
					<NewFormSenderForm
						formData={formData}
						loading={updateSenderMutation.isPending || isFetching}
						onFormChange={handleFormChange}
						onSubmit={handleSubmit}
						onReset={handleReset}
					/>
				</DialogScrollableArea>
			</DialogContent>
		</Dialog>
	);
};

export default SendersEditDialog;
